import { useEffect, useState } from "react";
import { Moon, Sun, SunMoon } from "lucide-react";

const KEY = "mpl-mode";
const OPTIONS = [["auto", "Auto", SunMoon], ["day", "Jour", Sun], ["night", "Nuit", Moon]];

export const getMode = () => { const m = localStorage.getItem(KEY); return m === "day" || m === "night" ? m : "auto"; };

// Lever / coucher du soleil approximés là où se trouve la personne : coordonnées déduites du fuseau horaire de l'appareil (aucune géolocalisation demandée).
const PLACES = { "Europe/Paris": [48.85, 2.35], "Europe/Brussels": [50.85, 4.35], "Europe/Zurich": [47.4, 8.5], "Europe/Luxembourg": [49.6, 6.1], "Europe/Monaco": [43.7, 7.4],
  "America/Toronto": [43.7, -79.4], "America/Montreal": [45.5, -73.6], "America/Vancouver": [49.3, -123.1], "America/Edmonton": [53.5, -113.5], "America/Winnipeg": [49.9, -97.1],
  "America/Regina": [50.4, -104.6], "America/Halifax": [44.6, -63.6], "America/Moncton": [46.1, -64.8], "America/St_Johns": [47.6, -52.7], "America/Guadeloupe": [16.2, -61.5],
  "America/Martinique": [14.6, -61], "America/Cayenne": [4.9, -52.3], "Indian/Reunion": [-21.1, 55.5], "Pacific/Noumea": [-22.3, 166.4], "Pacific/Tahiti": [-17.5, -149.6] };
function place(d) {
  let tz = "";
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { tz = ""; }
  if (PLACES[tz]) return PLACES[tz];
  const std = Math.max(new Date(d.getFullYear(), 0, 1).getTimezoneOffset(), new Date(d.getFullYear(), 6, 1).getTimezoneOffset());
  return [46, -std / 4];
}
function isDaylight(d = new Date()) {
  const rad = Math.PI / 180, [latDeg, lon] = place(d), lat = latDeg * rad;
  const doy = Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 864e5);
  const decl = 23.44 * rad * Math.sin((2 * Math.PI / 365) * (doy - 81));
  const half = Math.acos(Math.max(-1, Math.min(1, (Math.sin(-0.833 * rad) - Math.sin(lat) * Math.sin(decl)) / (Math.cos(lat) * Math.cos(decl))))) / rad / 15;
  const b = (2 * Math.PI / 364) * (doy - 81), eot = (9.87 * Math.sin(2 * b) - 7.53 * Math.cos(b) - 1.5 * Math.sin(b)) / 60;
  const noon = 12 - eot + (-d.getTimezoneOffset() / 60) - lon / 15;
  const h = d.getHours() + d.getMinutes() / 60;
  return h >= noon - half && h < noon + half;
}

export function applyMode(pref = getMode()) {
  const resolved = pref === "auto" ? (isDaylight() ? "day" : "night") : pref;
  document.documentElement.dataset.mode = resolved;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", resolved === "day" ? "#FFFDF5" : "#080C0D");
  return resolved;
}

export function initMode() {
  applyMode();
  setInterval(() => { if (getMode() === "auto" && !document.documentElement.dataset.surface) applyMode(); }, 5 * 60 * 1000);
  document.addEventListener("visibilitychange", () => { if (!document.hidden && getMode() === "auto" && !document.documentElement.dataset.surface) applyMode(); });
}

// Photo-led public pages are designed night-only until the public site redesign: force night while mounted.
export function useNightSurface() {
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.surface = "night";
    root.dataset.mode = "night";
    return () => { delete root.dataset.surface; applyMode(); };
  }, []);
}

export function ModeToggle({ compact = false, testId = "mode-toggle" }) {
  const [mode, setModeState] = useState(getMode);
  useEffect(() => {
    const sync = () => setModeState(getMode());
    window.addEventListener("mpl-mode", sync);
    return () => window.removeEventListener("mpl-mode", sync);
  }, []);
  const setMode = (m) => { if (m === "auto") localStorage.removeItem(KEY); else localStorage.setItem(KEY, m); applyMode(m); setModeState(m); window.dispatchEvent(new Event("mpl-mode")); };
  if (compact) {
    const i = OPTIONS.findIndex(([k]) => k === mode);
    const [, label, Icon] = OPTIONS[i];
    const next = OPTIONS[(i + 1) % OPTIONS.length];
    return (
      <button type="button" onClick={() => setMode(next[0])} aria-label={`Mode d'affichage : ${label}${mode === "auto" ? " (selon l'heure du jour)" : ""}. Passer en ${next[1]}`} title={mode === "auto" ? "Mode : Auto, selon l'heure du jour là où tu es" : `Mode : ${label}`}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-slate-300 transition-colors hover:bg-white/5" data-testid={testId} data-mode-pref={mode}>
        <Icon size={16} />
      </button>
    );
  }
  return (
    <div role="radiogroup" aria-label="Mode d'affichage" className="inline-flex rounded-full border border-white/10 p-1" data-testid={testId}>
      {OPTIONS.map(([k, label, Icon]) => (
        <button key={k} type="button" role="radio" aria-checked={mode === k} onClick={() => setMode(k)}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${mode === k ? "bg-[#B7F52A] text-[#05070D]" : "text-slate-300 hover:bg-white/5"}`}
          data-testid={`${testId}-${k}`}><Icon size={13} /> {label}</button>
      ))}
    </div>
  );
}
